import React, { useEffect, useRef, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { fileUrl, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Play, Pause, RotateCcw, Crown, Trash2, ChevronLeft, Undo2, FileText, Download, Flag, Clock as ClockIcon, Plus, Minus, X, Trophy } from "lucide-react";
import { toast } from "sonner";

const fmtClock = (sec) => {
  const m = Math.floor(Math.max(0, sec) / 60);
  const s = Math.max(0, sec) % 60;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
};

const COLOR_PRESETS = [
  "#FFFFFF", "#FFD60A", "#0A84FF", "#FF3B30", "#30D158",
  "#5E5CE6", "#FF9F0A", "#000000", "#8E8E93", "#BF5AF2",
  "#FF2D92", "#64D2FF",
];

function luminance(hex) {
  if (!hex || hex[0] !== "#") return 1;
  const h = hex.replace("#", "");
  const r = parseInt(h.substring(0, 2), 16) || 0;
  const g = parseInt(h.substring(2, 4), 16) || 0;
  const b = parseInt(h.substring(4, 6), 16) || 0;
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255;
}

function CenterControl({ label, color, colorB, onA, onB, testid }) {
  const textA = luminance(color) > 0.6 ? "#0A0A0A" : "#FFFFFF";
  const textB = luminance(colorB) > 0.6 ? "#0A0A0A" : "#FFFFFF";
  return (
    <div>
      <div className="text-center text-[9px] font-heading uppercase tracking-[0.25em] text-ink-muted mb-1">{label}</div>
      <div className="grid grid-cols-2 gap-1">
        <button
          type="button"
          data-testid={`${testid}-A`}
          onClick={onA}
          className="h-11 rounded-sm border font-heading font-bold uppercase tracking-widest text-xs transition-all hover:scale-[1.03]"
          style={{ background: color, color: textA, borderColor: color }}
        >A</button>
        <button
          type="button"
          data-testid={`${testid}-B`}
          onClick={onB}
          className="h-11 rounded-sm border font-heading font-bold uppercase tracking-widest text-xs transition-all hover:scale-[1.03]"
          style={{ background: colorB, color: textB, borderColor: colorB }}
        >B</button>
      </div>
    </div>
  );
}

function ColorPicker({ label, value, onChange, testid }) {
  return (
    <div>
      <div className="text-[10px] font-heading uppercase tracking-[0.3em] text-orange mb-2">{label}</div>
      <div className="flex items-center gap-2 mb-2">
        <div
          className="w-14 h-10 rounded-sm border border-navy-border shrink-0"
          style={{ background: value }}
          data-testid={`${testid}-preview`}
        />
        <input
          data-testid={`${testid}-input`}
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="bg-navy border border-navy-border rounded-sm font-mono text-sm h-10 px-3 text-white flex-1 min-w-0 focus:outline-none focus:border-orange"
        />
      </div>
      <div className="grid grid-cols-6 gap-1">
        {COLOR_PRESETS.map((c) => (
          <button
            key={c}
            type="button"
            data-testid={`${testid}-preset-${c.replace('#', '').toLowerCase()}`}
            onClick={() => onChange(c)}
            className={`h-7 rounded-sm border-2 transition-all ${value === c ? "border-orange scale-110" : "border-navy-border"}`}
            style={{ background: c }}
            title={c}
          />
        ))}
      </div>
    </div>
  );
}

const LS_KEY = (gid) => `gps_offline_actions_${gid}`;

export default function LiveOperator() {
  const { gameId } = useParams();
  const nav = useNavigate();
  const [game, setGame] = useState(null);
  const [teamA, setTeamA] = useState(null);
  const [teamB, setTeamB] = useState(null);
  const [playersA, setPlayersA] = useState([]);
  const [playersB, setPlayersB] = useState([]);
  const [actions, setActions] = useState([]);
  const [pendingAction, setPendingAction] = useState(null); // {step:"...", data:{}}
  const [setupDialog, setSetupDialog] = useState(false);
  const [setupColorA, setSetupColorA] = useState("#FFFFFF");
  const [setupColorB, setSetupColorB] = useState("#FFD60A");
  const [stats, setStats] = useState({ per_player: {}, team: { A: {}, B: {} } });
  const [online, setOnline] = useState(navigator.onLine);
  const tickRef = useRef(null);

  // Initial load helper (no-op; superseded by init useEffect + reload)
  const reload = useCallback(async () => {
    const [ac, g, s] = await Promise.all([
      api.get(`/games/${gameId}/actions`),
      api.get(`/games/${gameId}`),
      api.get(`/games/${gameId}/stats`),
    ]);
    setActions(ac.data); setGame((cur) => ({ ...cur, ...g.data })); setStats(s.data);
  }, [gameId]);

  useEffect(() => {
    let mounted = true;
    const init = async () => {
      try {
        const gRes = await api.get(`/games/${gameId}`);
        const teRes = await api.get("/teams");
        const pRes = await api.get("/players");
        const acRes = await api.get(`/games/${gameId}/actions`);
        const sRes = await api.get(`/games/${gameId}/stats`);
        if (!mounted) return;
        const teamMap = Object.fromEntries(teRes.data.map((t) => [t.id, t]));
        setGame(gRes.data);
        setTeamA(teamMap[gRes.data.team_a_id] || null);
        setTeamB(teamMap[gRes.data.team_b_id] || null);
        setPlayersA(pRes.data.filter((p) => p.team_id === gRes.data.team_a_id));
        setPlayersB(pRes.data.filter((p) => p.team_id === gRes.data.team_b_id));
        setActions(acRes.data);
        setStats(sRes.data);
      } catch (e) {
        toast.error("Failed to load game");
      }
    };
    init();
    const onOnline = () => { setOnline(true); flushOffline(); };
    const onOffline = () => setOnline(false);
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    return () => { mounted = false; window.removeEventListener("online", onOnline); window.removeEventListener("offline", onOffline); };
  }, [gameId]);

  // Clock countdown
  useEffect(() => {
    if (!game) return;
    if (game.clock_running) {
      tickRef.current = setInterval(() => {
        setGame((g) => {
          if (!g || !g.clock_running) return g;
          const next = (g.clock_seconds || 0) - 1;
          if (next <= 0) {
            return { ...g, clock_seconds: 0, clock_running: false };
          }
          return { ...g, clock_seconds: next };
        });
      }, 1000);
    }
    return () => { if (tickRef.current) { clearInterval(tickRef.current); tickRef.current = null; } };
  }, [game?.clock_running]);

  // Persist game state to backend on clock changes (debounced)
  useEffect(() => {
    if (!game) return;
    const t = setTimeout(() => {
      api.post(`/games/${gameId}/clock`, {
        clock_seconds: game.clock_seconds,
        clock_running: game.clock_running,
      }).catch(() => {});
    }, 800);
    return () => clearTimeout(t);
  }, [game?.clock_seconds, game?.clock_running, gameId]);

  const flushOffline = async () => {
    const queue = JSON.parse(localStorage.getItem(LS_KEY(gameId)) || "[]");
    if (queue.length === 0) return;
    for (const a of queue) {
      try { await api.post(`/games/${gameId}/actions`, a); } catch (_) { return; }
    }
    localStorage.removeItem(LS_KEY(gameId));
    toast.success("Synced offline actions");
    reload();
  };

  const recordAction = async (payload) => {
    const action = { ...payload, clock: fmtClock(game.clock_seconds || 0), score_a: game.score_a, score_b: game.score_b };
    if (!navigator.onLine) {
      const queue = JSON.parse(localStorage.getItem(LS_KEY(gameId)) || "[]");
      queue.push(action);
      localStorage.setItem(LS_KEY(gameId), JSON.stringify(queue));
      toast.warning("Saved offline — will sync");
      return;
    }
    try {
      await api.post(`/games/${gameId}/actions`, action);
      reload();
    } catch (e) {
      toast.error("Failed to record. Saved offline.");
      const queue = JSON.parse(localStorage.getItem(LS_KEY(gameId)) || "[]");
      queue.push(action);
      localStorage.setItem(LS_KEY(gameId), JSON.stringify(queue));
    }
  };

  const undoLast = async () => {
    if (actions.length === 0) return;
    const last = actions[actions.length - 1];
    if (!window.confirm("Undo last action?")) return;
    await api.delete(`/actions/${last.id}`);
    toast.success("Reverted");
    reload();
  };

  const deleteAction = async (aid) => {
    if (!window.confirm("Delete this action?")) return;
    await api.delete(`/actions/${aid}`);
    reload();
  };

  // ---------- Action workflows ----------
  const onPlayerClick = (team, player) => {
    setPendingAction({ step: "choose_action", team, player });
  };

  const startTurnoverTeam = (team) => {
    setPendingAction({ step: "turnover_team_12s", team });
  };

  // ---------- Game controls ----------
  const needsSetup = () => !game.possession || !game.color_a || !game.color_b;

  const openSetup = () => {
    setSetupColorA(game.color_a || teamA?.color || "#FFFFFF");
    setSetupColorB(game.color_b || teamB?.color || "#FFD60A");
    setSetupDialog(true);
  };

  const toggleClock = () => {
    if (!game.clock_running && needsSetup() && game.status !== "finished") {
      openSetup();
      return;
    }
    setGame((g) => ({ ...g, clock_running: !g.clock_running }));
  };
  const adjustClock = (deltaSeconds) => {
    if (game.clock_running) {
      toast.warning("Pare o relógio antes de ajustar");
      return;
    }
    setGame((g) => {
      const next = Math.max(0, Math.min(20 * 60, (g.clock_seconds || 0) + deltaSeconds));
      return { ...g, clock_seconds: next };
    });
  };
  const resetClock = () => {
    if (!window.confirm("Reset clock to 10:00?")) return;
    setGame((g) => ({ ...g, clock_seconds: 600, clock_running: false }));
  };
  const setPossession = async (side) => {
    setGame((g) => ({ ...g, possession: side }));
    api.post(`/games/${gameId}/clock`, { possession: side }).catch(() => {});
  };
  const confirmGameSetup = async (firstPoss) => {
    setSetupDialog(false);
    try {
      // Persist game colors + first possession via operator-accessible endpoint
      await api.post(`/games/${gameId}/setup`, {
        color_a: setupColorA,
        color_b: setupColorB,
        possession: firstPoss,
      });
      if (game.status === "scheduled") {
        await api.post(`/games/${gameId}/start`);
      }
      setGame((g) => ({
        ...g,
        possession: firstPoss,
        color_a: setupColorA,
        color_b: setupColorB,
        status: g.status === "scheduled" ? "live" : g.status,
        clock_running: true,
      }));
      toast.success(`Primeira posse: Time ${firstPoss}`);
    } catch (e) {
      toast.error("Falha ao salvar configurações do jogo");
    }
  };
  const startGame = async () => {
    if (needsSetup()) {
      openSetup();
      return;
    }
    await api.post(`/games/${gameId}/start`);
    reload();
  };
  const goOvertime = async () => {
    if (!window.confirm("End regulation and start Overtime (first to 2)?")) return;
    await api.post(`/games/${gameId}/overtime`);
    setGame((g) => ({ ...g, clock_seconds: 0, clock_running: true, status: "overtime", overtime: true }));
    reload();
  };
  const finishGame = async () => {
    if (!window.confirm("Finish this game?")) return;
    await api.post(`/games/${gameId}/finish`);
    reload();
  };

  if (!game) {
    return <div className="min-h-screen bg-navy text-white grid place-items-center font-heading uppercase tracking-widest">Loading console…</div>;
  }

  const activeA = (game.active_a || []);
  const activeB = (game.active_b || []);
  // Per-game colors (chosen at game setup) take precedence over team default color
  const colorA = game.color_a || teamA?.color || "#FFFFFF";
  const colorB = game.color_b || teamB?.color || "#FFD60A";

  return (
    <div className="min-h-screen bg-navy text-white flex flex-col">
      {/* Top control bar */}
      <header className="border-b border-navy-border bg-navy-deep flex items-center justify-between px-4 py-2">
        <div className="flex items-center gap-3">
          <Link to="/admin/games" className="flex items-center gap-1 text-ink-muted hover:text-orange text-xs uppercase tracking-widest font-heading" data-testid="exit-operator">
            <ChevronLeft size={14}/> Exit console
          </Link>
          <span className="font-mono text-xs text-ink-muted">Game {gameId.slice(0,8)}</span>
          <span className={`px-2 py-0.5 text-[10px] font-heading uppercase tracking-widest rounded-sm ${
            game.status === "live" ? "bg-court-red text-white animate-pulse" :
            game.status === "overtime" ? "bg-orange text-white" :
            game.status === "finished" ? "bg-navy-surface text-ink-secondary" :
            "border border-navy-border text-ink-muted"
          }`}>{game.status}</span>
          <span className={`text-[10px] font-mono uppercase tracking-widest ${online ? "text-court-green" : "text-court-red"}`}>● {online ? "online" : "offline"}</span>
        </div>
        <div className="flex items-center gap-2">
          <Button
            data-testid="open-setup-btn"
            onClick={openSetup}
            variant="outline"
            size="sm"
            className="bg-transparent border-navy-border text-white hover:bg-navy-surface hover:text-orange font-heading uppercase tracking-widest text-xs rounded-sm"
          >
            Setup
          </Button>
          <a href={`/scoreboard/${gameId}`} target="_blank" rel="noreferrer">
            <Button
              data-testid="open-scoreboard-btn"
              variant="outline"
              size="sm"
              className="bg-transparent border-navy-border text-white hover:bg-navy-surface hover:text-orange font-heading uppercase tracking-widest text-xs rounded-sm"
            >
              <Trophy size={12} className="mr-1" /> Scoreboard
            </Button>
          </a>
          {game.status === "scheduled" && (
            <Button data-testid="start-game-btn" onClick={startGame} className="bg-court-green hover:bg-court-green/90 text-white font-heading uppercase tracking-widest text-xs rounded-sm">
              <Play size={14} className="mr-1" /> Start game
            </Button>
          )}
          {game.status === "live" && game.score_a === game.score_b && (
            <Button data-testid="overtime-btn" onClick={goOvertime} variant="outline" className="bg-transparent border-orange text-orange hover:bg-orange hover:text-white font-heading uppercase tracking-widest text-xs rounded-sm">
              <Flag size={14} className="mr-1" /> Go to OT
            </Button>
          )}
          {(game.status === "live" || game.status === "overtime") && (
            <Button data-testid="finish-game-btn" onClick={finishGame} variant="outline" className="bg-transparent border-court-red text-court-red hover:bg-court-red hover:text-white font-heading uppercase tracking-widest text-xs rounded-sm">
              Finish
            </Button>
          )}
          <a href={`${API}/games/${gameId}/boxscore.pdf`} target="_blank" rel="noreferrer">
            <Button data-testid="op-boxscore-pdf" variant="outline" className="bg-transparent border-navy-border text-white hover:bg-navy-surface hover:text-orange font-heading uppercase tracking-widest text-xs rounded-sm">
              <Download size={12} className="mr-1" /> Box
            </Button>
          </a>
          <a href={`${API}/games/${gameId}/playbyplay.pdf`} target="_blank" rel="noreferrer">
            <Button data-testid="op-pbp-pdf" variant="outline" className="bg-transparent border-navy-border text-white hover:bg-navy-surface hover:text-orange font-heading uppercase tracking-widest text-xs rounded-sm">
              <FileText size={12} className="mr-1" /> PBP
            </Button>
          </a>
        </div>
      </header>

      {/* Main grid */}
      <div className="flex-1 grid grid-cols-12 gap-3 p-3 overflow-hidden">
        {/* TEAM A */}
        <TeamPanel
          side="A"
          team={teamA}
          color={colorA}
          players={playersA}
          activeIds={activeA}
          stats={stats.per_player}
          possession={game.possession === "A"}
          fouls={game.fouls_a}
          onPlayerClick={onPlayerClick}
          onTeamTurnover={() => startTurnoverTeam("A")}
        />

        {/* CENTER */}
        <div className="col-span-4 flex flex-col gap-3 min-h-0">
          <div className="bg-navy-deep border border-navy-border rounded-sm p-4 text-center">
            <div className="grid grid-cols-3 items-center">
              <div>
                <div className="font-heading uppercase tracking-tight text-sm" style={{ color: colorA }}>{teamA?.name || "A"}</div>
                <div className="font-mono tabular text-7xl font-black text-white leading-none mt-1" data-testid="score-a">{game.score_a}</div>
                <div className={`mt-1 font-heading uppercase tracking-widest ${game.fouls_a >= 6 ? "text-court-red" : "text-ink-secondary"}`}>
                  <span className="text-xs">Faltas</span>
                  <span className="font-mono tabular font-black text-xl md:text-2xl ml-1.5">{game.fouls_a}</span>
                  {game.fouls_a >= 6 && <span className="ml-1 text-court-red font-bold text-xs">⚠</span>}
                </div>
              </div>
              <div className="flex flex-col items-center">
                <div className="text-[10px] font-heading uppercase tracking-[0.3em] text-ink-muted">{game.overtime ? "Overtime" : "Period"}</div>
                <div className="font-mono tabular text-5xl font-black text-orange leading-none" data-testid="clock">{fmtClock(game.clock_seconds)}</div>
                <div className="mt-2 flex items-center gap-1">
                  <Button data-testid="clock-toggle-btn" onClick={toggleClock} size="sm" className="bg-orange hover:bg-orange-hover text-white text-xs rounded-sm font-heading uppercase tracking-widest">
                    {game.clock_running ? <><Pause size={12} className="mr-1"/>Stop</> : <><Play size={12} className="mr-1"/>Start</>}
                  </Button>
                  <Button data-testid="clock-reset-btn" onClick={resetClock} size="sm" variant="outline" className="bg-transparent border-navy-border text-white hover:bg-navy-surface hover:text-orange text-xs rounded-sm font-heading uppercase tracking-widest">
                    <RotateCcw size={12} className="mr-1"/>10:00
                  </Button>
                </div>
                {/* Clock editor: +/- minutes and seconds */}
                <div className="mt-2 flex items-center justify-center gap-2 text-[10px] font-heading uppercase tracking-widest text-ink-muted">
                  <div className="flex items-center gap-1">
                    <span>Min</span>
                    <button data-testid="clock-min-minus" onClick={() => adjustClock(-60)} className="w-6 h-6 grid place-items-center bg-navy border border-navy-border rounded-sm hover:border-orange hover:text-orange transition-colors" disabled={game.clock_running}><Minus size={10}/></button>
                    <button data-testid="clock-min-plus" onClick={() => adjustClock(60)} className="w-6 h-6 grid place-items-center bg-navy border border-navy-border rounded-sm hover:border-orange hover:text-orange transition-colors" disabled={game.clock_running}><Plus size={10}/></button>
                  </div>
                  <div className="flex items-center gap-1">
                    <span>Sec</span>
                    <button data-testid="clock-sec-minus" onClick={() => adjustClock(-1)} className="w-6 h-6 grid place-items-center bg-navy border border-navy-border rounded-sm hover:border-orange hover:text-orange transition-colors" disabled={game.clock_running}><Minus size={10}/></button>
                    <button data-testid="clock-sec-plus" onClick={() => adjustClock(1)} className="w-6 h-6 grid place-items-center bg-navy border border-navy-border rounded-sm hover:border-orange hover:text-orange transition-colors" disabled={game.clock_running}><Plus size={10}/></button>
                  </div>
                </div>
              </div>
              <div>
                <div className="font-heading uppercase tracking-tight text-sm" style={{ color: colorB }}>{teamB?.name || "B"}</div>
                <div className="font-mono tabular text-7xl font-black text-white leading-none mt-1" data-testid="score-b">{game.score_b}</div>
                <div className={`mt-1 font-heading uppercase tracking-widest ${game.fouls_b >= 6 ? "text-court-red" : "text-ink-secondary"}`}>
                  <span className="text-xs">Faltas</span>
                  <span className="font-mono tabular font-black text-xl md:text-2xl ml-1.5">{game.fouls_b}</span>
                  {game.fouls_b >= 6 && <span className="ml-1 text-court-red font-bold text-xs">⚠</span>}
                </div>
              </div>
            </div>
            <div className="mt-3 flex items-center justify-center gap-2">
              <Button data-testid="possession-a" onClick={() => setPossession("A")} size="sm" variant={game.possession === "A" ? "default" : "outline"} className={`text-xs font-heading uppercase tracking-widest rounded-sm ${game.possession === "A" ? "bg-orange text-white" : "bg-transparent border-navy-border text-white hover:bg-navy-surface"}`}>
                ▲ Poss A
              </Button>
              <Button data-testid="possession-b" onClick={() => setPossession("B")} size="sm" variant={game.possession === "B" ? "default" : "outline"} className={`text-xs font-heading uppercase tracking-widest rounded-sm ${game.possession === "B" ? "bg-orange text-white" : "bg-transparent border-navy-border text-white hover:bg-navy-surface"}`}>
                Poss B ▲
              </Button>
              <Button data-testid="undo-btn" onClick={undoLast} size="sm" variant="outline" className="bg-transparent border-navy-border text-white hover:bg-navy-surface hover:text-orange text-xs rounded-sm font-heading uppercase tracking-widest" disabled={actions.length === 0}>
                <Undo2 size={12} className="mr-1"/>Undo
              </Button>
            </div>
          </div>

          {/* Game control buttons: Team Foul / Team Turnover / Timeout — centralized */}
          <div className="bg-navy-deep border border-navy-border rounded-sm p-2">
            <div className="grid grid-cols-3 gap-2">
              <CenterControl
                label="Team Foul"
                color={colorA}
                colorB={colorB}
                onA={() => setPendingAction({ step: "confirm_team_foul", team: "A" })}
                onB={() => setPendingAction({ step: "confirm_team_foul", team: "B" })}
                testid="team-foul"
              />
              <CenterControl
                label="Team Turnover"
                color={colorA}
                colorB={colorB}
                onA={() => startTurnoverTeam("A")}
                onB={() => startTurnoverTeam("B")}
                testid="team-turnover-center"
              />
              <CenterControl
                label="Timeout"
                color={colorA}
                colorB={colorB}
                onA={async () => { await recordAction({ type: "timeout", team: "A" }); toast.success("Timeout A"); }}
                onB={async () => { await recordAction({ type: "timeout", team: "B" }); toast.success("Timeout B"); }}
                testid="timeout"
              />
            </div>
          </div>

          {/* Action log */}
          <div className="bg-navy-deep border border-navy-border rounded-sm flex-1 flex flex-col overflow-hidden min-h-0">
            <div className="px-3 py-2 bg-navy-surface border-b border-navy-border flex items-center justify-between">
              <h3 className="font-heading uppercase tracking-widest text-orange text-xs flex items-center gap-2">
                <ClockIcon size={12}/> Action log
              </h3>
              <span className="text-[10px] font-mono uppercase tracking-widest text-ink-muted">{actions.length} events</span>
            </div>
            <div className="overflow-y-auto flex-1 divide-y divide-navy-border">
              {actions.length === 0 ? (
                <div className="p-6 text-center text-ink-muted text-sm">No events yet. Pick a player to record an action.</div>
              ) : [...actions].reverse().map((a) => (
                <div key={a.id} className="flex items-start gap-2 px-3 py-2 hover:bg-navy-surface/40 group">
                  <span className="font-mono tabular text-[10px] text-ink-muted w-12 shrink-0">{a.clock || "—"}</span>
                  <span className={`text-[10px] font-heading uppercase tracking-widest px-1.5 py-0.5 rounded-sm shrink-0 ${a.team === "A" ? "bg-orange/20 text-orange" : a.team === "B" ? "bg-navy-surface text-ink-secondary border border-navy-border" : "bg-transparent text-ink-muted"}`}>
                    {a.team || "—"}
                  </span>
                  <span className="text-xs flex-1">{describeAction(a, playersA, playersB)}</span>
                  <button onClick={() => deleteAction(a.id)} className="opacity-0 group-hover:opacity-100 text-ink-muted hover:text-court-red transition-opacity" data-testid={`delete-action-${a.id}`}>
                    <Trash2 size={12}/>
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* TEAM B */}
        <TeamPanel
          side="B"
          team={teamB}
          color={colorB}
          players={playersB}
          activeIds={activeB}
          stats={stats.per_player}
          possession={game.possession === "B"}
          fouls={game.fouls_b}
          onPlayerClick={onPlayerClick}
          onTeamTurnover={() => startTurnoverTeam("B")}
        />
      </div>

      {/* Workflow modal */}
      <ActionWorkflow
        state={pendingAction}
        setState={setPendingAction}
        game={game}
        playersA={playersA}
        playersB={playersB}
        teamA={teamA}
        teamB={teamB}
        activeA={activeA}
        activeB={activeB}
        record={recordAction}
        onDisqualify={async (side, playerId) => {
          // Remove from active roster
          const key = side === "A" ? "active_a" : "active_b";
          const newActive = (game[key] || []).filter((id) => id !== playerId);
          try {
            await api.post(`/games/${gameId}/setup`, { [key]: newActive });
            setGame((g) => ({ ...g, [key]: newActive }));
            toast.success("Jogador desqualificado e removido do jogo");
          } catch (e) {
            toast.error("Falha ao desqualificar jogador");
          }
        }}
      />

      {/* Game setup dialog: choose colors + first possession */}
      <Dialog open={setupDialog} onOpenChange={setSetupDialog}>
        <DialogContent className="bg-navy-deep border-navy-border text-white max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading uppercase tracking-tight text-2xl text-center">
              Game Setup
            </DialogTitle>
          </DialogHeader>
          <p className="text-ink-secondary text-center text-sm -mt-2">
            Configure as cores das equipes para este jogo e escolha quem tem a primeira posse.
          </p>

          <div className="grid grid-cols-2 gap-6 mt-2">
            <ColorPicker label={teamA?.name || "Time A"} value={setupColorA} onChange={setSetupColorA} testid="setup-color-a" />
            <ColorPicker label={teamB?.name || "Time B"} value={setupColorB} onChange={setSetupColorB} testid="setup-color-b" />
          </div>

          <div>
            <div className="text-[10px] font-heading uppercase tracking-[0.3em] text-orange mb-2">First Possession</div>
            <div className="grid grid-cols-2 gap-3">
              <button
                data-testid="first-poss-A"
                onClick={() => confirmGameSetup("A")}
                className="h-24 flex flex-col items-center justify-center rounded-sm border-2 transition-all hover:scale-[1.02]"
                style={{ background: setupColorA, color: luminance(setupColorA) > 0.6 ? "#0A0A0A" : "#FFFFFF", borderColor: setupColorA }}
              >
                <span className="text-[10px] font-heading uppercase tracking-[0.3em] opacity-70">Time A</span>
                <span className="font-heading font-black uppercase tracking-tight text-2xl">{teamA?.name || "A"}</span>
              </button>
              <button
                data-testid="first-poss-B"
                onClick={() => confirmGameSetup("B")}
                className="h-24 flex flex-col items-center justify-center rounded-sm border-2 transition-all hover:scale-[1.02]"
                style={{ background: setupColorB, color: luminance(setupColorB) > 0.6 ? "#0A0A0A" : "#FFFFFF", borderColor: setupColorB }}
              >
                <span className="text-[10px] font-heading uppercase tracking-[0.3em] opacity-70">Time B</span>
                <span className="font-heading font-black uppercase tracking-tight text-2xl">{teamB?.name || "B"}</span>
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function TeamPanel({ side, team, color, players, activeIds, stats, possession, fouls, onPlayerClick, onTeamTurnover }) {
  const sorted = [...players].sort((a, b) => a.jersey_number - b.jersey_number);
  const teamColor = color || team?.color || "#FFFFFF";
  // Determine readable text color for contrast (simple luminance heuristic)
  const hex = teamColor.replace("#", "");
  const r = parseInt(hex.substring(0, 2), 16) || 255;
  const g = parseInt(hex.substring(2, 4), 16) || 255;
  const b = parseInt(hex.substring(4, 6), 16) || 255;
  const lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  const onColor = lum > 0.6 ? "#0A0A0A" : "#FFFFFF";

  return (
    <div className="col-span-4 flex flex-col bg-navy-deep border-2 rounded-sm overflow-hidden min-h-0" style={{ borderColor: teamColor }}>
      <div className="flex items-center justify-between px-3 py-2 border-b border-navy-border" style={{ background: teamColor, color: onColor }}>
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-sm bg-navy-deep border border-navy-border grid place-items-center overflow-hidden">
            {team?.logo_url ? <img src={fileUrl(team.logo_url)} alt="" className="w-full h-full object-cover" />
              : <span className="font-heading font-black text-orange text-sm">{team?.name?.[0] || side}</span>}
          </div>
          <div>
            <div className="font-heading font-black uppercase tracking-tight text-base leading-none">{team?.name || `Team ${side}`}</div>
            <div className="text-xs font-mono uppercase tracking-widest opacity-80">Side {side} · Faltas <span className="font-black text-base">{fouls}</span>{fouls >= 6 && <span className="ml-1 text-court-red font-bold">⚠</span>}</div>
          </div>
        </div>
        {possession && <span className="px-2 py-0.5 text-[10px] font-heading uppercase tracking-widest bg-orange text-white rounded-sm">Possession</span>}
      </div>

      <div className="flex-1 overflow-y-auto p-2 space-y-2 min-h-0">
        {sorted.length === 0 ? (
          <div className="text-center text-ink-muted text-sm p-6">No players</div>
        ) : sorted.map((p) => {
          const isActive = activeIds.includes(p.id);
          const s = stats[p.id] || {};
          return (
            <button
              key={p.id}
              type="button"
              data-testid={`player-${side}-${p.jersey_number}`}
              disabled={!isActive}
              onClick={() => isActive && onPlayerClick(side, p)}
              className={`w-full flex items-center gap-3 p-2 rounded-sm border transition-all text-left relative overflow-hidden
                ${isActive ? "bg-navy hover:border-orange border-navy-border cursor-pointer" : "bg-navy/50 border-navy-border opacity-40 cursor-not-allowed"}`}
              style={isActive ? { borderLeftWidth: "4px", borderLeftColor: teamColor } : undefined}
            >
              <div className="w-12 h-12 rounded-sm grid place-items-center shrink-0" style={{ background: teamColor, color: onColor }}>
                <span className="font-mono tabular text-2xl font-black leading-none">{p.jersey_number}</span>
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-heading uppercase tracking-tight text-white truncate text-sm">{p.full_name}</div>
                <div className="font-mono tabular text-[10px] text-ink-muted">
                  PTS {s.pts || 0} · AST {s.ast || 0} · REB {(s.reb || 0)} · STL {s.stl || 0} · PF {s.pf || 0}
                </div>
              </div>
              {!isActive && <span className="text-[9px] font-heading uppercase tracking-widest text-ink-muted">Bench</span>}
            </button>
          );
        })}
      </div>

      <div className="border-t border-navy-border p-2">
        <div className={`text-center font-heading uppercase tracking-widest ${fouls >= 6 ? "text-court-red" : "text-white"}`}>
          <span className="text-xs md:text-sm text-ink-muted">Faltas</span>
          <span className="font-mono tabular font-black text-2xl md:text-3xl ml-2">{fouls}</span>
          {fouls >= 6 && <div className="text-court-red text-xs font-bold mt-1">⚠ BÔNUS 7+ (2 LL)</div>}
        </div>
      </div>
    </div>
  );
}

function describeAction(a, playersA, playersB) {
  const find = (id) => playersA.find((p) => p.id === id) || playersB.find((p) => p.id === id);
  const p = find(a.player_id);
  const who = p ? `#${p.jersey_number} ${p.full_name}` : (a.team ? `Team ${a.team}` : "");
  if (a.type === "made_shot") {
    const ast = find(a.assist_player_id);
    return <span>{who} <span className="text-court-green font-bold">made {a.points}PT</span>{ast && <> · AST #{ast.jersey_number}</>}</span>;
  }
  if (a.type === "missed_shot") return <span>{who} <span className="text-ink-muted">missed {a.points}PT</span></span>;
  if (a.type === "rebound") return <span>{who} · {a.rebound_kind} rebound</span>;
  if (a.type === "foul") {
    const fp = find(a.fouled_player_id);
    return <span>{who} · <span className="text-court-red">{(a.foul_type || "personal").toUpperCase()} foul</span>{fp && <> on #{fp.jersey_number}</>}</span>;
  }
  if (a.type === "turnover") return <span>{who} · turnover{a.twelve_second ? " (12-sec)" : ""}</span>;
  if (a.type === "steal") return <span>{who} · <span className="text-court-green">steal</span></span>;
  if (a.type === "block") return <span>{who} · <span className="text-orange">block (toco)</span></span>;
  if (a.type === "timeout") return <span className="text-orange">Timeout · Team {a.team}</span>;
  return a.type;
}

// ---------------- Workflow Dialog ----------------
function WorkflowStep({ state, title, children }) {
  return (
    <DialogContent className="bg-navy-deep border-navy-border text-white max-w-xl">
      <DialogHeader>
        <DialogTitle className="font-heading uppercase tracking-tight text-xl">
          {state.player && <span className="text-orange mr-2">#{state.player.jersey_number}</span>}
          {state.player ? state.player.full_name : `Team ${state.team}`} — {title}
        </DialogTitle>
      </DialogHeader>
      <div className="space-y-4">{children}</div>
    </DialogContent>
  );
}

function WorkflowActionBtn({ label, onClick, color = "default", testid }) {
  return (
    <Button
      data-testid={testid}
      onClick={onClick}
      className={`h-20 font-heading uppercase tracking-widest text-base rounded-sm ${
        color === "green" ? "bg-court-green hover:bg-court-green/90 text-white" :
        color === "red" ? "bg-court-red hover:bg-court-red/90 text-white" :
        color === "orange" ? "bg-orange hover:bg-orange-hover text-white" :
        "bg-navy border border-navy-border text-white hover:border-orange"
      }`}
    >
      {label}
    </Button>
  );
}

function ActionWorkflow({ state, setState, game, playersA, playersB, teamA, teamB, activeA, activeB, record, onDisqualify }) {
  if (!state) return null;
  const teammates = state.team === "A" ? playersA : playersB;
  const opps = state.team === "A" ? playersB : playersA;
  // Players from the team that suffered the foul (opposite of fouler), filtered by active roster
  const opponentSide = state.team === "A" ? "B" : "A";
  const opponentActiveIds = opponentSide === "A" ? activeA : activeB;
  const opponentActivePlayers = opps.filter((p) => opponentActiveIds.includes(p.id));

  // Check if team fouls will reach 7+ after committing another common foul
  const currentTeamFouls = state.team === "A" ? (game?.fouls_a || 0) : (game?.fouls_b || 0);
  const willTriggerBonusFT = () => (currentTeamFouls + 1) >= 7;

  const close = () => setState(null);

  return (
    <Dialog open={!!state} onOpenChange={(v) => !v && close()}>
      {state.step === "choose_action" && (
        <WorkflowStep state={state} title="Select action">
          <div className="grid grid-cols-2 gap-3">
            <WorkflowActionBtn testid="act-made-1" label="1PT Made" color="green" onClick={() => setState({ ...state, step: "made_assist", points: 1 })} />
            <WorkflowActionBtn testid="act-made-2" label="2PT Made" color="green" onClick={() => setState({ ...state, step: "made_assist", points: 2 })} />
            <WorkflowActionBtn testid="act-miss-1" label="1PT Missed" onClick={async () => {
              await record({ type: "missed_shot", team: state.team, player_id: state.player.id, points: 1 });
              setState({ ...state, step: "rebound_pick", points: 1 });
            }} />
            <WorkflowActionBtn testid="act-miss-2" label="2PT Missed" onClick={async () => {
              await record({ type: "missed_shot", team: state.team, player_id: state.player.id, points: 2 });
              setState({ ...state, step: "rebound_pick", points: 2 });
            }} />
            <WorkflowActionBtn testid="act-rebound" label="Rebound" onClick={() => setState({ ...state, step: "rebound_kind" })} />
            <WorkflowActionBtn testid="act-assist" label="Assist (no shot)" onClick={async () => {
              await record({ type: "made_shot", team: state.team, player_id: null, assist_player_id: state.player.id, points: 0, note: "assist only" });
              close();
            }} />
            <WorkflowActionBtn testid="act-foul" label="Foul" color="red" onClick={() => setState({ ...state, step: "foul_type" })} />
            <WorkflowActionBtn testid="act-turnover" label="Turnover" onClick={async () => {
              await record({ type: "turnover", team: state.team, player_id: state.player.id, turnover_kind: "player" });
              // Ask about steal
              setState({ ...state, step: "steal_pick" });
            }} />
            <WorkflowActionBtn testid="act-steal" label="Steal" onClick={async () => {
              await record({ type: "steal", team: state.team, player_id: state.player.id });
              close();
            }} />
          </div>
        </WorkflowStep>
      )}

      {state.step === "made_assist" && (
        <WorkflowStep state={state} title={`${state.points}PT made — assist?`}>
          <PlayerGrid
            players={teammates.filter((p) => p.id !== state.player.id)}
            testidPrefix="assist"
            onPick={async (assistP) => {
              await record({ type: "made_shot", team: state.team, player_id: state.player.id, points: state.points, assist_player_id: assistP?.id || null });
              close();
            }}
          />
          <Button data-testid="no-assist-btn" variant="outline" onClick={async () => {
            await record({ type: "made_shot", team: state.team, player_id: state.player.id, points: state.points });
            close();
          }} className="w-full bg-transparent border-navy-border text-white hover:bg-navy-surface font-heading uppercase tracking-widest rounded-sm">
            No assist
          </Button>
        </WorkflowStep>
      )}

      {state.step === "rebound_pick" && (
        <ReboundPickerStep
          state={state}
          setState={setState}
          record={record}
          close={close}
          game={game}
          playersA={playersA}
          playersB={playersB}
          activeA={activeA}
          activeB={activeB}
          teamA={teamA}
          teamB={teamB}
        />
      )}

      {state.step === "tipin_made_pick" && (
        <TipInPickerStep
          state={state}
          setState={setState}
          record={record}
          close={close}
          game={game}
          shooterTeamPlayers={teammates.filter((p) => (state.team === "A" ? activeA : activeB).includes(p.id))}
          made={true}
        />
      )}

      {state.step === "tipin_missed_pick" && (
        <TipInPickerStep
          state={state}
          setState={setState}
          record={record}
          close={close}
          game={game}
          shooterTeamPlayers={teammates.filter((p) => (state.team === "A" ? activeA : activeB).includes(p.id))}
          made={false}
        />
      )}

      {state.step === "block_pick" && (
        <BlockPickerStep
          state={state}
          setState={setState}
          record={record}
          close={close}
          game={game}
          opponentPlayers={opps.filter((p) => (opponentSide === "A" ? activeA : activeB).includes(p.id))}
          opponentSide={opponentSide}
        />
      )}

      {state.step === "rebound_kind" && (
        <WorkflowStep state={state} title="Direct rebound">
          <div className="grid grid-cols-2 gap-2">
            <WorkflowActionBtn testid="reb-direct-off" label="Offensive" color="orange" onClick={async () => {
              await record({ type: "rebound", team: state.team, player_id: state.player.id, rebound_kind: "offensive" });
              close();
            }} />
            <WorkflowActionBtn testid="reb-direct-def" label="Defensive" onClick={async () => {
              await record({ type: "rebound", team: state.team, player_id: state.player.id, rebound_kind: "defensive" });
              close();
            }} />
          </div>
        </WorkflowStep>
      )}

      {state.step === "foul_type" && (
        <WorkflowStep state={state} title="Foul type">
          <div className="grid grid-cols-2 gap-2">
            <WorkflowActionBtn testid="foul-personal" label="Personal" color="red" onClick={() => setState({ ...state, step: "foul_on", foulType: "personal" })} />
            <WorkflowActionBtn testid="foul-offensive" label="Offensive" color="red" onClick={() => setState({ ...state, step: "foul_on", foulType: "offensive" })} />
            <WorkflowActionBtn testid="foul-act_of_shooting" label="Act of Shooting" color="red" onClick={() => setState({ ...state, step: "act_shooting_ft_count", foulType: "act_of_shooting" })} />
            <WorkflowActionBtn testid="foul-technical" label="Technical (1 FT)" color="red" onClick={async () => {
              await record({ type: "foul", team: state.team, player_id: state.player.id, foul_type: "technical", foul_target: "player" });
              setState({ ...state, step: "ft_shooter", foulType: "technical", ftCount: 1, ftIndex: 1 });
            }} />
            <WorkflowActionBtn testid="foul-unsportsmanlike" label="Unsportsmanlike (2 FT)" color="red" onClick={async () => {
              await record({ type: "foul", team: state.team, player_id: state.player.id, foul_type: "unsportsmanlike", foul_target: "player" });
              setState({ ...state, step: "ft_shooter", foulType: "unsportsmanlike", ftCount: 2, ftIndex: 1 });
            }} />
            <WorkflowActionBtn testid="foul-disqualifying" label="Disqualifying (2 FT)" color="red" onClick={async () => {
              await record({ type: "foul", team: state.team, player_id: state.player.id, foul_type: "disqualifying", foul_target: "player" });
              if (onDisqualify) await onDisqualify(state.team, state.player.id);
              setState({ ...state, step: "ft_shooter", foulType: "disqualifying", ftCount: 2, ftIndex: 1 });
            }} />
          </div>
        </WorkflowStep>
      )}

      {state.step === "act_shooting_ft_count" && (
        <WorkflowStep state={state} title="Act of Shooting — quantos lances livres?">
          <div className="grid grid-cols-2 gap-2">
            <WorkflowActionBtn testid="act-shooting-1ft" label="1 Lance Livre" color="orange" onClick={async () => {
              await record({ type: "foul", team: state.team, player_id: state.player.id, foul_type: "act_of_shooting", foul_target: "player" });
              const ftCount = willTriggerBonusFT() ? 2 : 1;
              setState({ ...state, step: "ft_shooter", ftCount, ftIndex: 1 });
            }} />
            <WorkflowActionBtn testid="act-shooting-2ft" label="2 Lances Livres" color="orange" onClick={async () => {
              await record({ type: "foul", team: state.team, player_id: state.player.id, foul_type: "act_of_shooting", foul_target: "player" });
              setState({ ...state, step: "ft_shooter", ftCount: 2, ftIndex: 1 });
            }} />
          </div>
        </WorkflowStep>
      )}

      {state.step === "foul_on" && (
        <WorkflowStep state={state} title={`${state.foulType.toUpperCase()} foul — fouled player?`}>
          <PlayerGrid
            players={opps}
            testidPrefix="foul-on"
            onPick={async (fp) => {
              await record({ type: "foul", team: state.team, player_id: state.player.id, foul_type: state.foulType, fouled_player_id: fp?.id || null, foul_target: "player" });
              // Bonus FT rule: 7+ team fouls → 2 free throws for opponent
              if (willTriggerBonusFT()) {
                setState({ ...state, step: "ft_shooter", ftCount: 2, ftIndex: 1, bonusFT: true });
              } else {
                close();
              }
            }}
          />
          <Button data-testid="foul-no-target" variant="outline" onClick={async () => {
            await record({ type: "foul", team: state.team, player_id: state.player.id, foul_type: state.foulType, foul_target: "player" });
            if (willTriggerBonusFT()) {
              setState({ ...state, step: "ft_shooter", ftCount: 2, ftIndex: 1, bonusFT: true });
            } else {
              close();
            }
          }} className="w-full bg-transparent border-navy-border text-white hover:bg-navy-surface font-heading uppercase tracking-widest rounded-sm">
            No specific target
          </Button>
        </WorkflowStep>
      )}

      {state.step === "confirm_team_foul" && (
        <WorkflowStep state={state} title={`Team foul — Team ${state.team}?`}>
          <p className="text-ink-secondary text-sm -mt-1">Falta coletiva (não atribuída a jogador). {(state.team === "A" ? (game?.fouls_a || 0) : (game?.fouls_b || 0)) + 1 >= 7 && <span className="text-orange font-bold">Time chegou a 7+ faltas — 2 LL automáticos.</span>}</p>
          <div className="grid grid-cols-2 gap-2">
            <WorkflowActionBtn testid="team-foul-confirm" label="Confirmar" color="red" onClick={async () => {
              await record({ type: "foul", team: state.team, player_id: null, foul_type: "team", foul_target: "team" });
              // Same 7+ bonus rule (using the game state at the moment before this action bumped fouls)
              if (willTriggerBonusFT()) {
                setState({ ...state, step: "ft_shooter", ftCount: 2, ftIndex: 1, bonusFT: true });
              } else {
                close();
              }
            }} />
            <WorkflowActionBtn testid="team-foul-cancel" label="Cancelar" onClick={close} />
          </div>
        </WorkflowStep>
      )}

      {state.step === "steal_pick" && (
        <WorkflowStep state={state} title={`Steal? Escolha o jogador da Team ${opponentSide}`}>
          <PlayerGrid
            players={opponentActivePlayers}
            testidPrefix="steal-pick"
            onPick={async (stealer) => {
              if (stealer) {
                await record({ type: "steal", team: opponentSide, player_id: stealer.id });
              }
              close();
            }}
          />
          <Button data-testid="no-steal-btn" variant="outline" onClick={close} className="w-full bg-transparent border-navy-border text-white hover:bg-navy-surface font-heading uppercase tracking-widest rounded-sm">
            No steal
          </Button>
        </WorkflowStep>
      )}

      {state.step === "ft_shooter" && (
        <WorkflowStep state={state} title={`Lance Livre — escolha o arremessador (Time ${opponentSide})`}>
          <p className="text-ink-secondary text-sm -mt-1">Total: {state.ftCount} {state.ftCount === 1 ? "lance" : "lances"} livre{state.ftCount > 1 ? "s" : ""}</p>
          <PlayerGrid
            players={opponentActivePlayers}
            testidPrefix="ft-shooter"
            onPick={(shooter) => {
              setState({ ...state, step: "ft_resolve", shooter, ftIndex: 1 });
            }}
          />
        </WorkflowStep>
      )}

      {state.step === "ft_resolve" && state.shooter && (
        <WorkflowStep state={state} title={`Lance livre ${state.ftIndex} de ${state.ftCount} · #${state.shooter.jersey_number} ${state.shooter.full_name}`}>
          <div className="grid grid-cols-2 gap-2">
            <WorkflowActionBtn testid="ft-made" label="Convertido" color="green" onClick={async () => {
              await record({ type: "made_shot", team: opponentSide, player_id: state.shooter.id, points: 1, note: "free throw" });
              if (state.ftIndex >= state.ftCount) close();
              else setState({ ...state, ftIndex: state.ftIndex + 1 });
            }} />
            <WorkflowActionBtn testid="ft-miss" label="Errado" color="red" onClick={async () => {
              await record({ type: "missed_shot", team: opponentSide, player_id: state.shooter.id, points: 1, note: "free throw" });
              if (state.ftIndex >= state.ftCount) close();
              else setState({ ...state, ftIndex: state.ftIndex + 1 });
            }} />
          </div>
        </WorkflowStep>
      )}

      {state.step === "turnover_team_12s" && (
        <WorkflowStep state={state} title="Team turnover">
          <div className="grid grid-cols-2 gap-2">
            <WorkflowActionBtn testid="to-12s-yes" label="12-second violation" color="orange" onClick={async () => {
              await record({ type: "turnover", team: state.team, turnover_kind: "team", twelve_second: true });
              setState({ ...state, step: "steal_pick" });
            }} />
            <WorkflowActionBtn testid="to-12s-no" label="Other team TO" onClick={async () => {
              await record({ type: "turnover", team: state.team, turnover_kind: "team", twelve_second: false });
              setState({ ...state, step: "steal_pick" });
            }} />
          </div>
        </WorkflowStep>
      )}
    </Dialog>
  );
}

function PlayerGrid({ players, onPick, testidPrefix }) {
  return (
    <div className="grid grid-cols-4 gap-2">
      {players.map((p) => (
        <button
          key={p.id}
          type="button"
          data-testid={`${testidPrefix}-${p.jersey_number}`}
          onClick={() => onPick(p)}
          className="aspect-square flex flex-col items-center justify-center bg-navy border border-navy-border rounded-sm hover:border-orange hover:bg-navy-surface transition-all p-1"
        >
          <span className="font-mono tabular text-3xl font-black text-orange leading-none">{p.jersey_number}</span>
          <span className="text-[9px] font-heading uppercase tracking-widest text-white mt-1 truncate w-full text-center px-1">{p.full_name.split(" ").pop()}</span>
        </button>
      ))}
    </div>
  );
}

// -------- Team-colored player button (used for rebound/tip-in/block flows) --------
function TeamColoredPlayerBtn({ player, color, testid, onClick }) {
  const textColor = luminance(color) > 0.6 ? "#0A0A0A" : "#FFFFFF";
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={onClick}
      className="aspect-square flex flex-col items-center justify-center rounded-sm transition-all hover:scale-105 hover:brightness-110 p-1 border-2"
      style={{ background: color, color: textColor, borderColor: color }}
    >
      <span className="font-mono tabular text-3xl font-black leading-none">{player.jersey_number}</span>
      <span className="text-[9px] font-heading uppercase tracking-widest mt-1 truncate w-full text-center px-1">{player.full_name.split(" ").pop()}</span>
    </button>
  );
}

// -------- Rebound picker (all players + 5 team-colored actions) --------
function ReboundPickerStep({ state, setState, record, close, game, playersA, playersB, activeA, activeB, teamA, teamB }) {
  const colorA = game?.color_a || teamA?.color || "#FFFFFF";
  const colorB = game?.color_b || teamB?.color || "#FFD60A";
  const activePlayersA = playersA.filter((p) => activeA.includes(p.id));
  const activePlayersB = playersB.filter((p) => activeB.includes(p.id));
  const shooterTeam = state.team; // A or B
  const textA = luminance(colorA) > 0.6 ? "#0A0A0A" : "#FFFFFF";
  const textB = luminance(colorB) > 0.6 ? "#0A0A0A" : "#FFFFFF";

  const onPickPlayer = async (player, teamSide) => {
    const kind = teamSide === shooterTeam ? "offensive" : "defensive";
    await record({ type: "rebound", team: teamSide, player_id: player.id, rebound_kind: kind });
    close();
  };

  const onTeamRebound = async (teamSide) => {
    const kind = teamSide === shooterTeam ? "offensive" : "defensive";
    await record({ type: "rebound", team: teamSide, player_id: null, rebound_kind: kind });
    close();
  };

  return (
    <DialogContent className="bg-navy-deep border-navy-border text-white max-w-4xl">
      <DialogHeader>
        <DialogTitle className="font-heading uppercase tracking-tight text-xl">
          {state.player && <span className="text-orange mr-2">#{state.player.jersey_number}</span>}
          {state.player ? state.player.full_name : `Team ${state.team}`} — {state.points}PT missed · Quem pegou o rebote?
        </DialogTitle>
      </DialogHeader>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <div className="text-[10px] font-heading uppercase tracking-widest text-center mb-2 py-1 rounded-sm" style={{ background: colorA, color: textA }}>
            {teamA?.name || "Team A"}
          </div>
          <div className="grid grid-cols-2 gap-2" data-testid="rebound-pick-team-A">
            {activePlayersA.map((p) => (
              <TeamColoredPlayerBtn key={p.id} player={p} color={colorA} testid={`rebound-pick-A-${p.jersey_number}`} onClick={() => onPickPlayer(p, "A")} />
            ))}
          </div>
        </div>
        <div>
          <div className="text-[10px] font-heading uppercase tracking-widest text-center mb-2 py-1 rounded-sm" style={{ background: colorB, color: textB }}>
            {teamB?.name || "Team B"}
          </div>
          <div className="grid grid-cols-2 gap-2" data-testid="rebound-pick-team-B">
            {activePlayersB.map((p) => (
              <TeamColoredPlayerBtn key={p.id} player={p} color={colorB} testid={`rebound-pick-B-${p.jersey_number}`} onClick={() => onPickPlayer(p, "B")} />
            ))}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-5 gap-2 pt-2 border-t border-navy-border">
        <button
          type="button"
          data-testid="rebound-team-A"
          onClick={() => onTeamRebound("A")}
          className="h-16 font-heading uppercase tracking-widest text-xs rounded-sm border-2 transition-all hover:brightness-110"
          style={{ background: colorA, color: textA, borderColor: colorA }}
        >
          Rebote Coletivo<br/>{teamA?.name || "Team A"}
        </button>
        <button
          type="button"
          data-testid="rebound-team-B"
          onClick={() => onTeamRebound("B")}
          className="h-16 font-heading uppercase tracking-widest text-xs rounded-sm border-2 transition-all hover:brightness-110"
          style={{ background: colorB, color: textB, borderColor: colorB }}
        >
          Rebote Coletivo<br/>{teamB?.name || "Team B"}
        </button>
        <WorkflowActionBtn
          testid="rebound-tipin-made"
          label="Tip-In Made"
          color="green"
          onClick={() => setState({ ...state, step: "tipin_made_pick" })}
        />
        <WorkflowActionBtn
          testid="rebound-tipin-missed"
          label="Tip-In Missed"
          color="orange"
          onClick={() => setState({ ...state, step: "tipin_missed_pick" })}
        />
        <WorkflowActionBtn
          testid="rebound-block"
          label="Block (Toco)"
          color="red"
          onClick={() => setState({ ...state, step: "block_pick" })}
        />
      </div>
    </DialogContent>
  );
}

// -------- Tip-in picker (offensive team only) --------
function TipInPickerStep({ state, setState, record, close, game, shooterTeamPlayers, made }) {
  const shooterTeam = state.team;
  return (
    <DialogContent className="bg-navy-deep border-navy-border text-white max-w-xl">
      <DialogHeader>
        <DialogTitle className="font-heading uppercase tracking-tight text-xl">
          Tip-In {made ? "Made" : "Missed"} — quem tentou o tip-in? (Time {shooterTeam})
        </DialogTitle>
      </DialogHeader>
      <PlayerGrid
        players={shooterTeamPlayers}
        testidPrefix={made ? "tipin-made-pick" : "tipin-missed-pick"}
        onPick={async (tipper) => {
          if (!tipper) { close(); return; }
          // Offensive rebound by tipper
          await record({ type: "rebound", team: shooterTeam, player_id: tipper.id, rebound_kind: "offensive" });
          if (made) {
            await record({ type: "made_shot", team: shooterTeam, player_id: tipper.id, points: 1, note: "tip-in" });
            close();
          } else {
            await record({ type: "missed_shot", team: shooterTeam, player_id: tipper.id, points: 1, note: "tip-in" });
            // Cascade: reopen rebound picker with the tipper as the new "shooter"
            setState({ ...state, step: "rebound_pick", player: tipper, points: 1 });
          }
        }}
      />
    </DialogContent>
  );
}

// -------- Block picker (defensive team only) --------
function BlockPickerStep({ state, setState, record, close, game, opponentPlayers, opponentSide }) {
  return (
    <DialogContent className="bg-navy-deep border-navy-border text-white max-w-xl">
      <DialogHeader>
        <DialogTitle className="font-heading uppercase tracking-tight text-xl">
          Block (Toco) — quem deu o toco? (Time {opponentSide})
        </DialogTitle>
      </DialogHeader>
      <PlayerGrid
        players={opponentPlayers}
        testidPrefix="block-pick"
        onPick={async (blocker) => {
          if (!blocker) { close(); return; }
          await record({ type: "block", team: opponentSide, player_id: blocker.id });
          // Cascade: reopen rebound picker (missed_shot already recorded, ball still loose)
          setState({ ...state, step: "rebound_pick" });
        }}
      />
    </DialogContent>
  );
}
